(() => {
  const elementName = 'wrist-match';

  if (customElements.get(elementName)) return;

  const storageKeys = {
    wristMm: 'mr_wristmatch_wrist_mm',
    preference: 'mr_wristmatch_preference',
    unit: 'mr_wristmatch_unit',
  };

  const units = {
    cm: { divisor: 10, max: 30, min: 10, placeholder: 'For example, 17', precision: 1, step: 0.1 },
    in: { divisor: 25.4, max: 11.81, min: 3.94, placeholder: 'For example, 6.7', precision: 2, step: 0.01 },
  };

  const preferences = {
    compact: { label: 'Compact', offset: -2.5 },
    balanced: { label: 'Balanced', offset: 0 },
    bold: { label: 'Bold', offset: 4 },
  };

  const getStoredValue = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  };

  const setStoredValue = (key, value) => {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (_error) {
      // WristMatch remains usable when storage is unavailable.
    }
  };

  const analyticsEvents = {
    started: 'mundo_relojero:wristmatch_started',
    profileCreated: 'mundo_relojero:wristmatch_profile_created',
    profileUpdated: 'mundo_relojero:wristmatch_profile_updated',
    resultViewed: 'mundo_relojero:wristmatch_result_viewed',
    journeyClicked: 'mundo_relojero:wristmatch_journey_clicked',
    calculatorProfileRestored: 'mundo_relojero:wristmatch_calculator_profile_restored',
    matchedProductClicked: 'mundo_relojero:wristmatch_matched_product_clicked',
    compareAdded: 'mundo_relojero:wristmatch_compare_added',
    compareOpened: 'mundo_relojero:wristmatch_compare_opened',
    compareProductClicked: 'mundo_relojero:wristmatch_compare_product_clicked',
    watchSaved: 'mundo_relojero:watch_saved',
    watchUnsaved: 'mundo_relojero:watch_unsaved',
    quickViewOpened: 'mundo_relojero:quick_view_opened',
  };
  const analyticsPropertyNames = new Set([
    'source',
    'preference',
    'match',
    'expected_wear',
    'fit_confidence',
    'display_unit',
    'wristmatch_used',
    'wristmatch_pick',
    'product_id',
    'product_handle',
    'variant_id',
    'visible_position',
    'profile_action',
  ]);
  const analyticsQueue = [];
  const queuedAnalyticsDedupeKeys = new Set();
  const publishedAnalyticsDedupeKeys = new Set();
  const analyticsQueueLimit = 50;
  const analyticsAvailabilityAttemptsLimit = 20;
  const analyticsAvailabilityDelay = 250;
  const analyticsPageId = String(window.performance?.timeOrigin || Date.now());
  let analyticsAvailabilityAttempts = 0;
  let analyticsAvailabilityTimer = null;

  const getAnalyticsPublisher = () => {
    const publisher = window.Shopify?.analytics?.publish;
    return typeof publisher === 'function' ? publisher.bind(window.Shopify.analytics) : null;
  };

  const getAnalyticsDedupeKey = (signature) => {
    let hash = 5381;
    for (let index = 0; index < signature.length; index += 1) {
      hash = ((hash << 5) + hash) ^ signature.charCodeAt(index);
    }
    return `mr_wristmatch_analytics_${(hash >>> 0).toString(36)}`;
  };

  const hasPublishedAnalyticsKey = (dedupeKey) => {
    if (!dedupeKey) return false;
    if (publishedAnalyticsDedupeKeys.has(dedupeKey)) return true;
    try {
      return window.sessionStorage.getItem(dedupeKey) === '1';
    } catch (_error) {
      return false;
    }
  };

  const markAnalyticsKeyPublished = (dedupeKey) => {
    if (!dedupeKey) return;
    publishedAnalyticsDedupeKeys.add(dedupeKey);
    try {
      window.sessionStorage.setItem(dedupeKey, '1');
    } catch (_error) {
      // In-memory deduplication remains available when session storage is unavailable.
    }
  };

  const sanitizeAnalyticsProperties = (properties) => Object.fromEntries(
    Object.entries(properties).filter(([name, value]) => (
      analyticsPropertyNames.has(name)
      && value !== undefined
      && value !== null
      && value !== ''
    ))
  );

  const sendAnalyticsEvent = ({ eventName, eventData, dedupeKey }) => {
    const publisher = getAnalyticsPublisher();
    if (!publisher) return false;

    try {
      const publishResult = publisher(eventName, eventData);
      if (publishResult && typeof publishResult.catch === 'function') publishResult.catch(() => {});
    } catch (_error) {
      // Analytics must never interrupt the WristMatch experience.
    }

    markAnalyticsKeyPublished(dedupeKey);
    return true;
  };

  const scheduleAnalyticsFlush = () => {
    if (analyticsAvailabilityTimer !== null || !analyticsQueue.length) return;
    analyticsAvailabilityTimer = window.setTimeout(() => {
      analyticsAvailabilityTimer = null;
      const publisherAvailable = Boolean(getAnalyticsPublisher());

      if (publisherAvailable) {
        analyticsAvailabilityAttempts = 0;
        while (analyticsQueue.length) {
          const queuedEvent = analyticsQueue.shift();
          queuedAnalyticsDedupeKeys.delete(queuedEvent.dedupeKey);
          sendAnalyticsEvent(queuedEvent);
        }
        return;
      }

      analyticsAvailabilityAttempts += 1;
      if (analyticsAvailabilityAttempts >= analyticsAvailabilityAttemptsLimit) {
        analyticsQueue.splice(0).forEach(({ dedupeKey }) => {
          queuedAnalyticsDedupeKeys.delete(dedupeKey);
        });
        analyticsAvailabilityAttempts = 0;
        return;
      }

      scheduleAnalyticsFlush();
    }, analyticsAvailabilityDelay);
  };

  const publishWristMatchEvent = (eventName, properties = {}, dedupeSignature = '') => {
    if (!Object.values(analyticsEvents).includes(eventName)) return;

    const dedupeKey = dedupeSignature ? getAnalyticsDedupeKey(`${eventName}|${dedupeSignature}`) : '';
    if (hasPublishedAnalyticsKey(dedupeKey) || queuedAnalyticsDedupeKeys.has(dedupeKey)) return;

    const analyticsEvent = {
      dedupeKey,
      eventData: sanitizeAnalyticsProperties(properties),
      eventName,
    };

    if (sendAnalyticsEvent(analyticsEvent)) return;

    if (analyticsQueue.length >= analyticsQueueLimit) {
      const droppedEvent = analyticsQueue.shift();
      queuedAnalyticsDedupeKeys.delete(droppedEvent.dedupeKey);
    }
    analyticsQueue.push(analyticsEvent);
    if (dedupeKey) queuedAnalyticsDedupeKeys.add(dedupeKey);
    scheduleAnalyticsFlush();
  };

  const trimNumber = (value, precision) => {
    const fixed = value.toFixed(precision);
    return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
  };

  const formatWristMeasurement = (wristMm, unit) => {
    const config = units[unit];
    const formatted = trimNumber(wristMm / config.divisor, config.precision);
    return `${formatted} ${unit === 'cm' ? 'cm' : 'in'}`;
  };

  const formatCaseSize = (value) => {
    const rounded = Math.round(value * 2) / 2;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const formatPublishedDimension = (value) => String(value);

  const getPreferenceTarget = (wristMm, preference) => wristMm * 0.235 + preferences[preference].offset;

  const getPreferenceRange = (wristMm, preference) => {
    const target = getPreferenceTarget(wristMm, preference);
    return {
      maximum: target + 2.5,
      minimum: target - 2.5,
    };
  };

  const parseDimension = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const preferenceMatches = {
    compact: {
      'Very compact': 'Good match',
      Compact: 'Excellent match',
      Balanced: 'Good match',
      Bold: 'Style stretch',
      'Very bold': 'Different from your preference',
    },
    balanced: {
      'Very compact': 'Style stretch',
      Compact: 'Good match',
      Balanced: 'Excellent match',
      Bold: 'Good match',
      'Very bold': 'Style stretch',
    },
    bold: {
      'Very compact': 'Different from your preference',
      Compact: 'Style stretch',
      Balanced: 'Good match',
      Bold: 'Excellent match',
      'Very bold': 'Good match',
    },
  };

  const shapeAdjustments = {
    round: 0,
    square: 0.8,
    rectangular: 0.3,
    tonneau: 0.5,
    octagonal: 0.6,
    cushion: 0.6,
    oval: 0.2,
    asymmetrical: 0.4,
  };

  const getShapeAdjustment = (product) => shapeAdjustments[product.shape.toLowerCase()] || 0;

  const getThicknessAdjustment = (product) => {
    if (!product.thickness) return 0;
    return Math.max(-0.8, Math.min(2, (product.thickness - 10) * 0.35));
  };

  const getLengthAdjustment = (product, wristMm) => {
    if (!product.length) return 0;

    const wristTopWidth = wristMm / Math.PI;
    const ratio = product.length / wristTopWidth;

    if (ratio < 0.82) return 0;
    if (ratio < 0.9) return 0.5;
    if (ratio < 0.95) return 1;
    if (ratio < 1) return 1.7;
    return 2.5;
  };

  const getEffectivePresence = (product, wristMm) => product.width
    + getShapeAdjustment(product)
    + getThicknessAdjustment(product)
    + getLengthAdjustment(product, wristMm);

  const getExpectedWear = (effectivePresence, wristMm) => {
    const balancedTarget = wristMm * 0.235;
    const delta = effectivePresence - balancedTarget;

    if (delta <= -6) return 'Very compact';
    if (delta < -2) return 'Compact';
    if (delta <= 2.5) return 'Balanced';
    if (delta <= 8) return 'Bold';
    return 'Very bold';
  };

  const getConfidence = (product) => {
    const supportingDimensions = [
      Boolean(product.length),
      Boolean(product.thickness),
      product.shape !== '',
    ].filter(Boolean).length;

    if (supportingDimensions === 3) return 'High';
    if (supportingDimensions >= 2) return 'Good';
    return 'Estimated';
  };

  const evaluateProduct = (product, wristMm, preference) => {
    if (!product.width) return null;

    const effectivePresence = getEffectivePresence(product, wristMm);
    const expectedWear = getExpectedWear(effectivePresence, wristMm);
    return {
      confidence: getConfidence(product),
      expectedWear,
      styleMatch: preferenceMatches[preference][expectedWear],
      targetDistance: Math.abs(effectivePresence - getPreferenceTarget(wristMm, preference)),
    };
  };

  const compareElementName = 'wristmatch-compare';
  const compareProductSelector = '[data-wristmatch-compare-product]';
  const compareToggleSelector = '[data-wristmatch-compare-toggle]';
  const compareStorageKey = 'mr_wristmatch_compare';
  const compareStorageVersion = 1;
  const compareMaximum = 3;
  const compareTieToleranceMm = 0.5;

  const cleanCompareString = (value, maximumLength = 500) => String(value || '').trim().slice(0, maximumLength);

  const getSafeCompareUrl = (value, { allowExternal = false } = {}) => {
    const candidate = cleanCompareString(value, 2000);
    if (!candidate) return '';

    try {
      const url = new URL(candidate, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      if (!allowExternal && url.origin !== window.location.origin) return '';
      return allowExternal ? url.href : `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return '';
    }
  };

  const normalizeCompareProduct = (product) => {
    if (!product || typeof product !== 'object') return null;

    const id = cleanCompareString(product.id, 80);
    const handle = cleanCompareString(product.handle, 255);
    const title = cleanCompareString(product.title, 500);
    const url = getSafeCompareUrl(product.url);
    const width = parseDimension(product.width);
    if (!id || !handle || !title || !url || !width) return null;

    return {
      available: product.available === true || product.available === 'true',
      handle,
      id,
      imageUrl: getSafeCompareUrl(product.imageUrl, { allowExternal: true }),
      length: parseDimension(product.length),
      priceText: cleanCompareString(product.priceText, 200),
      selectedAt: Number.isFinite(Number(product.selectedAt)) ? Number(product.selectedAt) : Date.now(),
      shape: cleanCompareString(product.shape, 100),
      thickness: parseDimension(product.thickness),
      title,
      url,
      width,
    };
  };

  const getCompareProductFromElement = (element) => normalizeCompareProduct({
    available: element?.dataset.productAvailable,
    handle: element?.dataset.productHandle,
    id: element?.dataset.productId,
    imageUrl: element?.dataset.productImage,
    length: element?.dataset.caseLength,
    priceText: element?.dataset.productPrice,
    shape: element?.dataset.caseShape,
    thickness: element?.dataset.caseThickness,
    title: element?.dataset.productTitle,
    url: element?.dataset.productUrl,
    width: element?.dataset.caseWidth,
  });

  const setCompareProductDataset = (element, product) => {
    if (!element || !product) return;
    element.dataset.wristmatchCompareProduct = '';
    element.dataset.productId = product.id;
    element.dataset.productHandle = product.handle;
    element.dataset.productUrl = product.url;
    element.dataset.productTitle = product.title;
    element.dataset.productImage = product.imageUrl;
    element.dataset.productPrice = product.priceText;
    element.dataset.productAvailable = String(product.available);
    element.dataset.caseWidth = String(product.width);
    element.dataset.caseLength = product.length ? String(product.length) : '';
    element.dataset.caseThickness = product.thickness ? String(product.thickness) : '';
    element.dataset.caseShape = product.shape;
  };

  const readCompareSelection = () => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(compareStorageKey));
      if (!stored || stored.version !== compareStorageVersion || !Array.isArray(stored.products)) {
        throw new Error('Unsupported WristMatch comparison data.');
      }
      if (stored.products.length > compareMaximum) throw new Error('Invalid WristMatch comparison size.');

      const products = stored.products.map(normalizeCompareProduct);
      const uniqueIds = new Set(products.filter(Boolean).map(({ id }) => id));
      if (products.some((product) => !product) || uniqueIds.size !== products.length) {
        throw new Error('Invalid WristMatch comparison data.');
      }
      return products;
    } catch (_error) {
      try {
        window.sessionStorage.removeItem(compareStorageKey);
      } catch (_storageError) {
        // Comparison remains available in memory when session storage is unavailable.
      }
      return [];
    }
  };

  const writeCompareSelection = (products) => {
    try {
      window.sessionStorage.setItem(compareStorageKey, JSON.stringify({
        products,
        version: compareStorageVersion,
      }));
    } catch (_error) {
      // Comparison remains available for the current page when session storage is unavailable.
    }
  };

  const getCompareSource = (element) => {
    const cardActionSource = element?.closest('[data-card-action-source]')?.dataset.cardActionSource;
    if (cardActionSource) return cardActionSource;
    if (element.closest('[data-wristmatch-page-candidate]')) return 'calculator';
    if (element.closest('[data-wristmatch-collection-product]')) return 'collection';
    return 'product';
  };

  const refreshCompareExperiences = () => {
    document.querySelectorAll(compareElementName).forEach((element) => element.refresh?.());
  };

  const recommendationSelector = 'product-recommendations.related-products';
  const recommendationItemSelector = '[data-wristmatch-recommendation]';
  const collectionItemSelector = '[data-wristmatch-collection-product]';
  const recommendationRanks = {
    'Excellent match': 0,
    'Good match': 1,
    'Style stretch': 2,
    'Different from your preference': 3,
  };
  let recommendationFrame = null;

  const getNormalizedProfileState = () => {
    const wristMm = Number.parseFloat(getStoredValue(storageKeys.wristMm));
    const savedPreference = getStoredValue(storageKeys.preference);
    const savedUnit = getStoredValue(storageKeys.unit);
    const preference = Object.prototype.hasOwnProperty.call(preferences, savedPreference)
      ? savedPreference
      : 'balanced';
    const unit = Object.prototype.hasOwnProperty.call(units, savedUnit) ? savedUnit : 'cm';
    const hasValidWrist = Number.isFinite(wristMm) && wristMm >= 100 && wristMm <= 300;

    if (hasValidWrist) {
      if (savedPreference !== preference) setStoredValue(storageKeys.preference, preference);
      if (savedUnit !== unit) setStoredValue(storageKeys.unit, unit);
    }

    return {
      preference,
      unit,
      wristMm: hasValidWrist ? wristMm : null,
    };
  };

  const getSavedProfile = () => {
    const profile = getNormalizedProfileState();
    return profile.wristMm === null ? null : profile;
  };

  const getRecommendationProduct = (item) => ({
    width: parseDimension(item.dataset.caseWidth),
    length: parseDimension(item.dataset.caseLength),
    thickness: parseDimension(item.dataset.caseThickness),
    shape: (item.dataset.caseShape || '').trim(),
  });

  const reorderRecommendationItems = (items, sortedItems) => {
    if (items.every((item, index) => item === sortedItems[index])) return;

    const list = items[0]?.parentElement;
    if (!list) return;

    const fragment = document.createDocumentFragment();
    sortedItems.forEach((item) => fragment.appendChild(item));
    list.appendChild(fragment);
  };

  const evaluateRecommendationSection = (section) => {
    const items = [...section.querySelectorAll(recommendationItemSelector)];
    if (!items.length) return;

    const profile = getSavedProfile();
    const personalization = section.querySelector('[data-wristmatch-personalization]');
    if (!profile) {
      if (personalization) {
        personalization.hidden = true;
        personalization.textContent = '';
      }
      delete section.dataset.wristmatchPersonalized;

      if (!items.some((item) => item.dataset.wristmatchOriginalOrder !== undefined)) return;

      items.forEach((item) => {
        const badge = item.querySelector('[data-wristmatch-pick-badge]');
        if (badge) badge.hidden = true;
        delete item.dataset.wristmatchMatch;
        delete item.dataset.wristmatchExpectedWear;
        delete item.dataset.wristmatchFitConfidence;
        delete item.dataset.wristmatchPick;
        delete item.dataset.wristmatchVisiblePosition;
      });
      const originalItems = [...items].sort(
        (first, second) => Number(first.dataset.wristmatchOriginalOrder) - Number(second.dataset.wristmatchOriginalOrder)
      );
      reorderRecommendationItems(items, originalItems);
      return;
    }

    if (personalization) {
      personalization.textContent = `Personalized for your ${formatWristMeasurement(profile.wristMm, profile.unit)} wrist and ${preferences[profile.preference].label} preference.`;
      personalization.hidden = false;
    }
    section.dataset.wristmatchPersonalized = 'true';

    items.forEach((item, index) => {
      if (item.dataset.wristmatchOriginalOrder === undefined) {
        item.dataset.wristmatchOriginalOrder = String(index);
      }
    });

    const evaluatedItems = items.map((item) => {
      const evaluation = evaluateProduct(getRecommendationProduct(item), profile.wristMm, profile.preference);
      const badge = item.querySelector('[data-wristmatch-pick-badge]');
      const isPick = evaluation?.styleMatch === 'Excellent match';
      if (badge) badge.hidden = !isPick;

      if (evaluation) {
        item.dataset.wristmatchMatch = evaluation.styleMatch;
        item.dataset.wristmatchExpectedWear = evaluation.expectedWear;
        item.dataset.wristmatchFitConfidence = evaluation.confidence;
        item.dataset.wristmatchPick = String(isPick);
      } else {
        delete item.dataset.wristmatchMatch;
        delete item.dataset.wristmatchExpectedWear;
        delete item.dataset.wristmatchFitConfidence;
        delete item.dataset.wristmatchPick;
      }

      return {
        item,
        originalOrder: Number(item.dataset.wristmatchOriginalOrder),
        rank: evaluation ? recommendationRanks[evaluation.styleMatch] : 4,
      };
    });

    evaluatedItems.sort((first, second) => first.rank - second.rank || first.originalOrder - second.originalOrder);
    evaluatedItems.forEach(({ item }, index) => {
      item.dataset.wristmatchVisiblePosition = String(index + 1);
    });
    reorderRecommendationItems(items, evaluatedItems.map(({ item }) => item));
  };

  const evaluateRecommendationSections = () => {
    document.querySelectorAll(recommendationSelector).forEach(evaluateRecommendationSection);
  };

  const evaluateCollectionProducts = () => {
    const profile = getSavedProfile();

    document.querySelectorAll(collectionItemSelector).forEach((item) => {
      const badge = item.querySelector('[data-wristmatch-collection-badge]');
      const evaluation = profile
        ? evaluateProduct(getRecommendationProduct(item), profile.wristMm, profile.preference)
        : null;
      const isStrongMatch = evaluation?.styleMatch === 'Excellent match'
        && ['High', 'Good'].includes(evaluation.confidence);

      if (badge) badge.hidden = !isStrongMatch;

      if (isStrongMatch) {
        item.dataset.wristmatchMatch = evaluation.styleMatch;
        item.dataset.wristmatchExpectedWear = evaluation.expectedWear;
        item.dataset.wristmatchFitConfidence = evaluation.confidence;
        item.dataset.wristmatchPick = 'false';
      } else {
        delete item.dataset.wristmatchMatch;
        delete item.dataset.wristmatchExpectedWear;
        delete item.dataset.wristmatchFitConfidence;
        delete item.dataset.wristmatchPick;
      }
    });
  };

  const scheduleRecommendationEvaluation = () => {
    if (recommendationFrame !== null) return;
    recommendationFrame = window.requestAnimationFrame(() => {
      recommendationFrame = null;
      evaluateRecommendationSections();
      evaluateCollectionProducts();
      refreshCompareExperiences();
    });
  };

  class WristMatch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.wristmatchInitialized === 'true') return;
      this.dataset.wristmatchInitialized = 'true';

      this.form = this.querySelector('[data-wristmatch-form]');
      this.submitButton = this.querySelector('[data-wristmatch-submit]');
      this.measurementInput = this.querySelector('[data-wristmatch-measurement]');
      this.unitInputs = [...this.querySelectorAll('[data-wristmatch-unit]')];
      this.preferenceInputs = [...this.querySelectorAll('[data-wristmatch-preference]')];
      this.help = this.querySelector('[data-wristmatch-help]');
      this.error = this.querySelector('[data-wristmatch-error]');
      this.successFeedback = this.querySelector('[data-wristmatch-success]');
      this.result = this.querySelector('[data-wristmatch-result]');
      this.resultHeading = this.querySelector('[data-wristmatch-result-heading]');
      this.rangeOutput = this.querySelector('[data-wristmatch-range]');
      this.presenceOutput = this.querySelector('[data-wristmatch-presence]');
      this.wristOutput = this.querySelector('[data-wristmatch-wrist]');
      this.productEvaluation = this.querySelector('[data-wristmatch-product-evaluation]');
      this.productUnavailable = this.querySelector('[data-wristmatch-unavailable]');
      this.expectedWearOutput = this.querySelector('[data-wristmatch-expected-wear]');
      this.advisoryOutput = this.querySelector('[data-wristmatch-advisory]');
      this.matchCheck = this.querySelector('[data-wristmatch-check]');
      this.savedWristOutput = this.querySelector('[data-wristmatch-saved-wrist]');
      this.savedPreferenceOutput = this.querySelector('[data-wristmatch-saved-preference]');
      this.confidenceOutput = this.querySelector('[data-wristmatch-confidence]');
      this.editButton = this.querySelector('[data-wristmatch-edit]');
      this.whyDetails = this.querySelector('[data-wristmatch-why]');
      this.whyEvaluation = this.querySelector('[data-wristmatch-why-evaluation]');
      this.whyExpectedWear = this.querySelector('[data-wristmatch-why-expected-wear]');
      this.whyPreference = this.querySelector('[data-wristmatch-why-preference]');
      this.whyMatch = this.querySelector('[data-wristmatch-why-match]');
      this.whyConfidence = this.querySelector('[data-wristmatch-why-confidence]');
      this.whyExplanation = this.querySelector('[data-wristmatch-why-explanation]');
      this.whyConfidenceNote = this.querySelector('[data-wristmatch-why-confidence-note]');
      this.recommendationLink = this.querySelector('[data-wristmatch-recommendation-slot]');
      this.journeyLinks = [...this.querySelectorAll('[data-wristmatch-journey-link]')];
      this.comparisonSection = this.querySelector('[data-wristmatch-comparison-section]');
      this.comparisonItems = [...this.querySelectorAll('[data-wristmatch-comparison]')];
      this.picksGrid = this.querySelector('[data-wristmatch-page-picks-grid]');
      this.picksPrompt = this.querySelector('[data-wristmatch-page-picks-prompt]');
      this.picksSubtitle = this.querySelector('[data-wristmatch-page-picks-subtitle]');
      this.catalogUrl = this.dataset.wristmatchCatalogUrl || '';
      this.catalogSectionId = this.dataset.wristmatchCatalogSectionId || 'wristmatch-catalog-data';
      this.cardSectionId = this.dataset.wristmatchCardSectionId || 'wristmatch-product-card';
      this.picksCount = Math.max(1, Number.parseInt(this.dataset.wristmatchPicksCount, 10) || 12);
      this.catalogCandidatesPromise = null;
      this.productCardMarkupCache = new Map();
      this.picksRequestId = 0;
      this.picksAbortController = null;

      if (!this.form || !this.measurementInput || !this.result) return;

      this.currentUnit = 'cm';
      this.mode = this.dataset.wristmatchMode === 'product'
        ? 'product'
        : this.dataset.wristmatchMode === 'calculator'
          ? 'calculator'
          : 'profile';
      this.product = {
        width: parseDimension(this.dataset.caseWidth),
        length: parseDimension(this.dataset.caseLength),
        thickness: parseDimension(this.dataset.caseThickness),
        shape: (this.dataset.caseShape || '').trim(),
      };
      this.restoreState();
      this.bindEvents();
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.trackStarted();
        this.updateResult({ focusResult: true });
      });

      this.measurementInput.addEventListener('input', () => {
        this.trackStarted();
        this.clearError();
        this.clearSuccessFeedback();
        this.result.hidden = true;
        if (this.mode === 'calculator') this.hideCalculatorResults();
      });

      this.unitInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            this.trackStarted();
            this.clearSuccessFeedback();
            this.changeUnit(input.value);
          }
        });
      });

      this.preferenceInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.trackStarted();
          this.clearSuccessFeedback();
          const previousProfile = getSavedProfile();
          if (this.mode !== 'product') setStoredValue(storageKeys.preference, input.value);
          if (this.mode !== 'product') {
            this.trackProfileChange(previousProfile, getSavedProfile());
          }
          if (!this.result.hidden && this.measurementInput.value !== '') {
            this.updateResult({ focusResult: false });
          }
        });
      });

      if (this.editButton) {
        this.editButton.addEventListener('click', () => this.showProductForm({ focusInput: true }));
      }

      this.journeyLinks.forEach((link) => {
        link.addEventListener('click', () => this.trackJourneyClicked());
      });
    }

    restoreState() {
      const profile = getNormalizedProfileState();
      const { preference, unit, wristMm } = profile;
      this.updateSubmitButton(wristMm !== null);
      this.selectRadio(this.unitInputs, unit);
      this.currentUnit = unit;
      this.applyUnitAttributes(unit);

      this.selectRadio(this.preferenceInputs, preference);

      if (wristMm !== null) {
        this.measurementInput.value = this.fromMillimeters(wristMm, unit);
        this.renderResult(wristMm, preference);
        this.trackResultViewed(wristMm, preference);
        if (this.mode === 'calculator') this.trackCalculatorProfileRestored(wristMm, preference);
      } else if (this.mode === 'product') {
        this.showProductForm({ focusInput: false });
      }
    }

    changeUnit(nextUnit) {
      if (!Object.prototype.hasOwnProperty.call(units, nextUnit) || nextUnit === this.currentUnit) return;

      const previousProfile = getSavedProfile();
      const currentValue = Number.parseFloat(this.measurementInput.value);
      const wristMm = Number.isFinite(currentValue)
        ? this.toMillimeters(currentValue, this.currentUnit)
        : null;

      this.currentUnit = nextUnit;
      this.applyUnitAttributes(nextUnit);
      if (this.mode !== 'product') setStoredValue(storageKeys.unit, nextUnit);
      if (this.mode !== 'product') this.trackProfileChange(previousProfile, getSavedProfile());

      if (wristMm !== null) this.measurementInput.value = this.fromMillimeters(wristMm, nextUnit);

      this.clearError();
      if (!this.result.hidden && this.measurementInput.value !== '') {
        this.updateResult({ focusResult: false });
      }
    }

    applyUnitAttributes(unit) {
      const config = units[unit];
      this.measurementInput.min = String(config.min);
      this.measurementInput.max = String(config.max);
      this.measurementInput.step = String(config.step);
      this.measurementInput.placeholder = config.placeholder;
      this.help.textContent = unit === 'cm'
        ? 'Enter a measurement between 10 and 30 cm.'
        : 'Enter a measurement between 3.94 and 11.81 inches.';
    }

    updateResult({ focusResult }) {
      const value = Number.parseFloat(this.measurementInput.value);
      const config = units[this.currentUnit];

      if (!Number.isFinite(value) || value < config.min || value > config.max) {
        this.showError(
          this.currentUnit === 'cm'
            ? 'Enter a wrist circumference between 10 and 30 cm.'
            : 'Enter a wrist circumference between 3.94 and 11.81 inches.'
        );
        return;
      }

      const preference = this.preferenceInputs.find((input) => input.checked)?.value || 'balanced';
      const wristMm = this.toMillimeters(value, this.currentUnit);
      const previousProfile = getSavedProfile();

      this.clearError();
      this.renderResult(wristMm, preference);
      setStoredValue(storageKeys.wristMm, wristMm.toFixed(2));
      setStoredValue(storageKeys.preference, preference);
      setStoredValue(storageKeys.unit, this.currentUnit);
      this.updateSubmitButton(true);
      this.trackProfileChange(previousProfile, { preference, unit: this.currentUnit, wristMm });
      this.trackResultViewed(wristMm, preference);
      scheduleRecommendationEvaluation();

      if (focusResult) {
        this.showSuccessFeedback(previousProfile ? 'updated' : 'created');
        this.result.focus({ preventScroll: true });
        if (this.mode === 'calculator') this.scrollToResult();
      }
    }

    renderResult(wristMm, preference) {
      if (this.mode === 'product') {
        this.renderProductResult(wristMm, preference);
        return;
      }

      const selectedPreference = preferences[preference];
      const range = getPreferenceRange(wristMm, preference);

      this.resultHeading.textContent = `${selectedPreference.label} presence`;
      this.rangeOutput.textContent = `${formatCaseSize(range.minimum)}–${formatCaseSize(range.maximum)} mm`;
      this.presenceOutput.textContent = `${selectedPreference.label} presence`;
      this.wristOutput.textContent = this.formatWrist(wristMm);
      this.result.hidden = false;

      if (this.mode === 'calculator') this.renderCalculatorResults(wristMm, preference);
    }

    renderCalculatorResults(wristMm, preference) {
      this.comparisonItems.forEach((item) => {
        const itemPreference = item.dataset.wristmatchComparison;
        if (!Object.prototype.hasOwnProperty.call(preferences, itemPreference)) return;

        const range = getPreferenceRange(wristMm, itemPreference);
        const output = item.querySelector('[data-wristmatch-comparison-range]');
        if (output) {
          output.textContent = `${formatCaseSize(range.minimum)}–${formatCaseSize(range.maximum)} mm`;
        }

        const isSelected = itemPreference === preference;
        item.dataset.wristmatchSelected = String(isSelected);
        if (isSelected) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });

      if (this.comparisonSection) this.comparisonSection.hidden = false;
      if (this.picksSubtitle) {
        this.picksSubtitle.textContent = `Based on your ${this.formatWrist(wristMm)} wrist and ${preferences[preference].label} preference.`;
        this.picksSubtitle.hidden = false;
      }
      this.renderCalculatorPicks(wristMm, preference);
    }

    async renderCalculatorPicks(wristMm, preference) {
      if (!this.picksGrid) return;

      this.cancelCalculatorCardRequests();
      const requestId = ++this.picksRequestId;
      const abortController = new AbortController();
      this.picksAbortController = abortController;
      this.showCalculatorPicksLoading();

      try {
        const candidates = await this.getCatalogCandidates();
        if (requestId !== this.picksRequestId) return;

        const rankedCandidates = this.selectCalculatorPicks(candidates, wristMm, preference);
        if (!rankedCandidates.length) {
          this.picksGrid.replaceChildren();
          this.picksGrid.hidden = true;
          this.finishCalculatorPicksLoading();
          this.showPicksPrompt('No suitable WristMatch picks are available from this collection yet.');
          return;
        }

        const loadedCards = await this.loadWinnerCards(
          rankedCandidates,
          this.picksCount,
          abortController.signal
        );
        if (requestId !== this.picksRequestId) return;

        let topPickCount = 0;
        const cardItems = loadedCards.map(({ candidate, cardMarkup, compareProduct, evaluation }, index) => {
          const isTopPick = evaluation.styleMatch === 'Excellent match' && topPickCount < 4;
          if (isTopPick) topPickCount += 1;
          return this.createCalculatorPickItem(
            candidate,
            cardMarkup,
            compareProduct,
            evaluation,
            isTopPick,
            index + 1
          );
        });

        this.picksGrid.replaceChildren(...cardItems);
        this.picksGrid.hidden = cardItems.length === 0;
        this.finishCalculatorPicksLoading();
        if (cardItems.length) {
          const completionMessage = cardItems.length === 1
            ? '1 watch matched to your WristMatch is ready.'
            : `${cardItems.length} watches matched to your WristMatch are ready.`;
          this.showPicksPrompt(completionMessage, { visuallyHidden: true });
        } else {
          this.showPicksPrompt('Personalized product cards are temporarily unavailable.');
        }
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.picksRequestId) return;
        this.picksGrid.replaceChildren();
        this.picksGrid.hidden = true;
        this.finishCalculatorPicksLoading();
        this.showPicksPrompt('Personalized picks are temporarily unavailable.');
      } finally {
        if (this.picksAbortController === abortController) this.picksAbortController = null;
      }
    }

    showCalculatorPicksLoading() {
      const hasExistingPicks = Boolean(
        this.picksGrid.querySelector('[data-wristmatch-page-candidate]')
      );
      this.picksGrid.setAttribute('aria-busy', 'true');
      this.picksGrid.dataset.wristmatchUpdating = 'true';
      this.picksGrid.hidden = false;

      if (!this.picksGrid.children.length) {
        const skeletons = Array.from({ length: this.picksCount }, () => {
          const item = document.createElement('li');
          item.className = 'grid__item';
          item.dataset.wristmatchPageSkeleton = '';
          item.setAttribute('aria-hidden', 'true');
          return item;
        });
        this.picksGrid.replaceChildren(...skeletons);
      }

      this.showPicksPrompt('Finding your strongest WristMatch picks…', {
        visuallyHidden: hasExistingPicks,
      });
    }

    finishCalculatorPicksLoading() {
      this.picksGrid.setAttribute('aria-busy', 'false');
      delete this.picksGrid.dataset.wristmatchUpdating;
    }

    showPicksPrompt(message, { visuallyHidden = false } = {}) {
      if (!this.picksPrompt) return;
      this.picksPrompt.classList.toggle('visually-hidden', visuallyHidden);
      this.picksPrompt.textContent = message;
      this.picksPrompt.hidden = false;
    }

    cancelCalculatorCardRequests() {
      if (!this.picksAbortController) return;
      this.picksAbortController.abort();
      this.picksAbortController = null;
    }

    getCatalogCandidates() {
      if (!this.catalogCandidatesPromise) {
        this.catalogCandidatesPromise = this.fetchCatalogCandidates().catch((error) => {
          this.catalogCandidatesPromise = null;
          throw error;
        });
      }
      return this.catalogCandidatesPromise;
    }

    async fetchCatalogCandidates() {
      if (!this.catalogUrl) throw new Error('WristMatch candidate collection is unavailable.');

      const candidates = [];
      const seenProducts = new Set();
      let totalPages = 1;

      for (let page = 1; page <= totalPages; page += 1) {
        const payload = await this.fetchCatalogPage(page);
        if (page > 1 && Number(payload.page) !== page) {
          throw new Error('WristMatch catalog pagination did not advance.');
        }

        totalPages = Math.min(100, Math.max(1, Number(payload.pages) || 1));
        const products = Array.isArray(payload.products) ? payload.products : [];

        products.forEach((product) => {
          const key = String(product.id || product.handle || '');
          if (!key || !product.handle || seenProducts.has(key)) return;
          seenProducts.add(key);
          candidates.push({
            available: product.available === true,
            caseLength: product.caseLength,
            caseShape: product.caseShape,
            caseThickness: product.caseThickness,
            caseWidth: product.caseWidth,
            handle: String(product.handle),
            id: product.id,
            originalOrder: candidates.length,
            title: String(product.title || ''),
          });
        });
      }

      return candidates;
    }

    async fetchCatalogPage(page) {
      try {
        return await this.fetchCatalogPageOnce(page);
      } catch (_error) {
        await new Promise((resolve) => window.setTimeout(resolve, 350));
        return this.fetchCatalogPageOnce(page);
      }
    }

    async fetchCatalogPageOnce(page) {
      const url = new URL(this.catalogUrl, window.location.origin);
      url.searchParams.set('section_id', this.catalogSectionId);
      url.searchParams.set('page', String(page));

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`WristMatch catalog request failed: ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const dataElement = documentFragment.querySelector('[data-wristmatch-catalog-page]');
      if (!dataElement) throw new Error('WristMatch catalog data was not returned.');
      return JSON.parse(dataElement.textContent);
    }

    selectCalculatorPicks(candidates, wristMm, preference) {
      const confidenceRanks = { High: 0, Good: 1, Estimated: 2 };
      const evaluated = candidates
        .filter((candidate) => candidate.available)
        .map((candidate) => {
          const product = {
            width: parseDimension(candidate.caseWidth),
            length: parseDimension(candidate.caseLength),
            thickness: parseDimension(candidate.caseThickness),
            shape: String(candidate.caseShape || '').trim(),
          };
          const evaluation = evaluateProduct(product, wristMm, preference);
          return evaluation ? { candidate, evaluation } : null;
        })
        .filter(Boolean);

      const excellent = evaluated
        .filter(({ evaluation }) => evaluation.styleMatch === 'Excellent match')
        .sort((first, second) => (
          confidenceRanks[first.evaluation.confidence] - confidenceRanks[second.evaluation.confidence]
          || first.evaluation.targetDistance - second.evaluation.targetDistance
          || first.candidate.originalOrder - second.candidate.originalOrder
        ));
      const good = evaluated.filter(({ evaluation }) => evaluation.styleMatch === 'Good match');
      const styleStretch = evaluated.filter(({ evaluation }) => evaluation.styleMatch === 'Style stretch');

      return [...excellent, ...good, ...styleStretch];
    }

    async loadWinnerCards(rankedCandidates, desiredCount, signal) {
      const results = [];
      let nextIndex = 0;

      while (nextIndex < rankedCandidates.length && results.length < desiredCount) {
        if (signal.aborted) throw new DOMException('The WristMatch request was cancelled.', 'AbortError');

        const remainingSlots = desiredCount - results.length;
        const batchSize = Math.min(4, remainingSlots, rankedCandidates.length - nextIndex);
        const batch = rankedCandidates.slice(nextIndex, nextIndex + batchSize);
        nextIndex += batchSize;

        const loadedBatch = await Promise.all(batch.map(async (winner) => {
          try {
            const productCard = await this.getProductCardMarkup(winner.candidate.handle, signal);
            return { ...winner, ...productCard };
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            return null;
          }
        }));

        loadedBatch.forEach((loadedCard) => {
          if (loadedCard && results.length < desiredCount) results.push(loadedCard);
        });
      }

      return results;
    }

    async getProductCardMarkup(handle, signal) {
      if (this.productCardMarkupCache.has(handle)) return this.productCardMarkupCache.get(handle);

      const productCard = await this.fetchProductCardMarkup(handle, signal);
      if (!signal.aborted) this.productCardMarkupCache.set(handle, productCard);
      return productCard;
    }

    async fetchProductCardMarkup(handle, signal) {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const url = new URL(`${routeRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
      url.searchParams.set('section_id', this.cardSectionId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal,
      });
      if (!response.ok) throw new Error(`WristMatch product card request failed: ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productCard = documentFragment.querySelector('[data-wristmatch-product-card]');
      const card = productCard?.querySelector('.card-wrapper');
      if (!card) throw new Error('WristMatch product card markup was not returned.');
      return {
        cardMarkup: card.outerHTML,
        compareProduct: getCompareProductFromElement(productCard),
      };
    }

    createCalculatorPickItem(candidate, cardMarkup, compareProduct, evaluation, isTopPick, visiblePosition) {
      const item = document.createElement('li');
      item.className = 'grid__item';
      item.dataset.wristmatchPageCandidate = '';
      item.dataset.productId = String(candidate.id || '');
      item.dataset.productHandle = candidate.handle || '';
      item.dataset.wristmatchMatch = evaluation.styleMatch;
      item.dataset.wristmatchExpectedWear = evaluation.expectedWear;
      item.dataset.wristmatchFitConfidence = evaluation.confidence;
      item.dataset.wristmatchPick = String(isTopPick);
      item.dataset.wristmatchVisiblePosition = String(visiblePosition);
      if (compareProduct) setCompareProductDataset(item, compareProduct);

      if (isTopPick) {
        const badge = document.createElement('span');
        badge.dataset.wristmatchPagePickBadge = '';
        badge.textContent = 'WristMatch pick';
        item.appendChild(badge);
      }

      const template = document.createElement('template');
      template.innerHTML = cardMarkup.trim();
      item.appendChild(template.content.cloneNode(true));
      return item;
    }

    hideCalculatorResults() {
      this.cancelCalculatorCardRequests();
      this.picksRequestId += 1;
      if (this.comparisonSection) this.comparisonSection.hidden = true;
      if (this.picksSubtitle) this.picksSubtitle.hidden = true;
      if (this.picksGrid) {
        this.picksGrid.hidden = true;
        this.picksGrid.setAttribute('aria-busy', 'false');
        delete this.picksGrid.dataset.wristmatchUpdating;
      }
      if (this.picksPrompt) {
        this.picksPrompt.classList.remove('visually-hidden');
        this.picksPrompt.textContent = 'Enter your wrist measurement above to see personalized picks.';
        this.picksPrompt.hidden = false;
      }
    }

    renderProductResult(wristMm, preference) {
      const evaluation = evaluateProduct(this.product, wristMm, preference);
      if (!evaluation) {
        this.renderProductUnavailable();
        return;
      }

      const selectedPreference = preferences[preference];
      const { confidence, expectedWear, styleMatch } = evaluation;

      this.resultHeading.textContent = styleMatch;
      this.matchCheck.hidden = !['Excellent match', 'Good match'].includes(styleMatch);
      this.expectedWearOutput.textContent = `${expectedWear} on your wrist`;
      this.advisoryOutput.textContent = this.getProductAdvice(styleMatch, expectedWear, preference);
      this.savedWristOutput.textContent = `${this.formatWrist(wristMm)} wrist`;
      this.savedPreferenceOutput.textContent = selectedPreference.label;
      this.confidenceOutput.textContent = confidence;
      this.renderProductExplanation(evaluation, preference);
      this.result.dataset.wristmatchMatch = styleMatch.toLowerCase().replace(/ /g, '-');
      this.productEvaluation.hidden = false;
      this.productUnavailable.hidden = true;
      if (this.recommendationLink) {
        this.recommendationLink.textContent = ['Excellent match', 'Good match'].includes(styleMatch)
          ? 'See watches matched to you'
          : 'See better WristMatch matches';
        this.recommendationLink.hidden = false;
      }
      this.showProductResult();
    }

    renderProductUnavailable() {
      this.resultHeading.textContent = 'WristMatch unavailable';
      this.matchCheck.hidden = true;
      this.result.dataset.wristmatchMatch = 'unavailable';
      this.productEvaluation.hidden = true;
      this.productUnavailable.hidden = false;
      if (this.whyEvaluation) this.whyEvaluation.hidden = true;
      if (this.whyExplanation) this.whyExplanation.hidden = true;
      if (this.whyConfidenceNote) this.whyConfidenceNote.hidden = true;
      if (this.recommendationLink) this.recommendationLink.hidden = true;
      this.showProductResult();
    }

    showProductForm({ focusInput }) {
      this.result.hidden = true;
      this.form.hidden = false;
      if (focusInput) this.measurementInput.focus();
    }

    showProductResult() {
      this.form.hidden = true;
      if (this.whyDetails) this.whyDetails.open = false;
      this.result.hidden = false;
    }

    getProductAdvice(styleMatch, expectedWear, preference) {
      if (styleMatch === 'Excellent match') return 'Right in your preferred range.';
      if (styleMatch === 'Different from your preference') {
        return 'Significantly different from the watch presence you selected.';
      }

      const wearRanks = { 'Very compact': 0, Compact: 1, Balanced: 2, Bold: 3, 'Very bold': 4 };
      const preferenceRanks = { compact: 1, balanced: 2, bold: 3 };
      const direction = wearRanks[expectedWear] < preferenceRanks[preference] ? 'more compact' : 'bolder';

      if (styleMatch === 'Good match') {
        return `A little ${direction} than your preference, but still a versatile match.`;
      }

      return `Noticeably ${direction} than your usual preference.`;
    }

    renderProductExplanation(evaluation, preference) {
      const selectedPreference = preferences[preference]?.label;
      if (!evaluation || !selectedPreference) return;

      if (this.whyExpectedWear) this.whyExpectedWear.textContent = `${evaluation.expectedWear} on your wrist`;
      if (this.whyPreference) this.whyPreference.textContent = selectedPreference;
      if (this.whyMatch) this.whyMatch.textContent = evaluation.styleMatch;
      if (this.whyConfidence) this.whyConfidence.textContent = evaluation.confidence;
      if (this.whyExplanation) {
        this.whyExplanation.textContent = this.getProductExplanation(evaluation, preference);
        this.whyExplanation.hidden = false;
      }
      if (this.whyConfidenceNote) {
        this.whyConfidenceNote.textContent = this.getConfidenceExplanation(evaluation.confidence);
        this.whyConfidenceNote.hidden = false;
      }
      if (this.whyEvaluation) this.whyEvaluation.hidden = false;
    }

    getProductExplanation(evaluation, preference) {
      const selectedPreference = preferences[preference].label;
      if (evaluation.styleMatch === 'Excellent match') {
        return `This watch is expected to wear close to your selected ${selectedPreference} presence based on its published case proportions.`;
      }

      const wearRanks = { 'Very compact': 0, Compact: 1, Balanced: 2, Bold: 3, 'Very bold': 4 };
      const preferenceRanks = { compact: 1, balanced: 2, bold: 3 };
      const direction = wearRanks[evaluation.expectedWear] < preferenceRanks[preference]
        ? 'more compactly'
        : 'more boldly';
      const degree = evaluation.styleMatch === 'Good match'
        ? 'a little'
        : evaluation.styleMatch === 'Style stretch' ? 'noticeably' : 'significantly';
      return `This watch is expected to wear ${degree} ${direction} than your selected ${selectedPreference} presence based on its published case proportions.`;
    }

    getConfidenceExplanation(confidence) {
      if (confidence === 'High') {
        return 'High confidence means the estimate is supported by the published case width, length, thickness and shape.';
      }
      if (confidence === 'Good') {
        return 'Good confidence means the estimate has supporting published dimensional data.';
      }
      return 'Estimated confidence means the result relies on limited published sizing information.';
    }

    getAnalyticsSource() {
      if (this.mode === 'product') return 'product';
      if (this.mode === 'calculator') return 'calculator';
      return 'home';
    }

    getAnalyticsIdentity() {
      const productIdentity = this.dataset.productId || this.dataset.productHandle || '';
      return productIdentity ? `${this.getAnalyticsSource()}|${productIdentity}` : this.getAnalyticsSource();
    }

    getAnalyticsProperties(preference, evaluation = null) {
      const properties = {
        source: this.getAnalyticsSource(),
        preference: preferences[preference]?.label,
        display_unit: this.currentUnit,
        wristmatch_used: true,
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        variant_id: this.dataset.variantId,
      };

      if (evaluation) {
        properties.match = evaluation.styleMatch;
        properties.expected_wear = evaluation.expectedWear;
        properties.fit_confidence = evaluation.confidence;
      }

      return properties;
    }

    trackStarted() {
      const preference = this.preferenceInputs.find((input) => input.checked)?.value || 'balanced';
      publishWristMatchEvent(
        analyticsEvents.started,
        this.getAnalyticsProperties(preference),
        this.getAnalyticsIdentity()
      );
    }

    trackProfileChange(previousProfile, nextProfile) {
      if (!nextProfile || nextProfile.wristMm === null) return;

      const action = previousProfile ? 'updated' : 'created';
      const unchanged = previousProfile
        && previousProfile.wristMm.toFixed(2) === nextProfile.wristMm.toFixed(2)
        && previousProfile.preference === nextProfile.preference
        && previousProfile.unit === nextProfile.unit;
      if (unchanged) return;

      const eventName = action === 'created'
        ? analyticsEvents.profileCreated
        : analyticsEvents.profileUpdated;
      publishWristMatchEvent(
        eventName,
        {
          ...this.getAnalyticsProperties(nextProfile.preference),
          display_unit: nextProfile.unit,
          profile_action: action,
        },
        action === 'created' ? 'first-valid-profile' : ''
      );
    }

    trackResultViewed(wristMm, preference) {
      const evaluation = this.mode === 'product'
        ? evaluateProduct(this.product, wristMm, preference)
        : null;
      if (this.mode === 'product' && !evaluation) return;

      const signature = [
        analyticsPageId,
        this.getAnalyticsIdentity(),
        wristMm.toFixed(2),
        preference,
        this.currentUnit,
        evaluation?.styleMatch || 'profile',
      ].join('|');
      publishWristMatchEvent(
        analyticsEvents.resultViewed,
        this.getAnalyticsProperties(preference, evaluation),
        signature
      );
    }

    trackCalculatorProfileRestored(wristMm, preference) {
      publishWristMatchEvent(
        analyticsEvents.calculatorProfileRestored,
        {
          ...this.getAnalyticsProperties(preference),
          profile_action: 'restored',
        },
        `${analyticsPageId}|${this.getAnalyticsIdentity()}|${wristMm.toFixed(2)}|${preference}|${this.currentUnit}`
      );
    }

    trackJourneyClicked() {
      const profile = getSavedProfile();
      if (!profile) return;
      const evaluation = this.mode === 'product'
        ? evaluateProduct(this.product, profile.wristMm, profile.preference)
        : null;
      publishWristMatchEvent(
        analyticsEvents.journeyClicked,
        this.getAnalyticsProperties(profile.preference, evaluation)
      );
    }

    updateSubmitButton(hasValidProfile) {
      if (this.mode === 'product' || !this.submitButton) return;
      this.submitButton.textContent = hasValidProfile
        ? 'Update my WristMatch'
        : 'Find my WristMatch';
    }

    showSuccessFeedback(action) {
      if (!this.successFeedback) return;
      this.successFeedback.textContent = action === 'created'
        ? 'WristMatch created — your experience is now personalized.'
        : 'WristMatch updated — your experience is now personalized.';
      this.successFeedback.hidden = false;
    }

    clearSuccessFeedback() {
      if (!this.successFeedback) return;
      this.successFeedback.textContent = '';
      this.successFeedback.hidden = true;
    }

    scrollToResult() {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.result.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    formatWrist(wristMm) {
      return formatWristMeasurement(wristMm, this.currentUnit);
    }

    fromMillimeters(wristMm, unit) {
      const config = units[unit];
      return trimNumber(wristMm / config.divisor, config.precision);
    }

    toMillimeters(value, unit) {
      return value * units[unit].divisor;
    }

    selectRadio(inputs, value) {
      const selected = inputs.find((input) => input.value === value);
      if (selected) selected.checked = true;
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.measurementInput.setAttribute('aria-invalid', 'true');
      this.measurementInput.focus();
    }

    clearError() {
      this.error.textContent = '';
      this.error.hidden = true;
      this.measurementInput.removeAttribute('aria-invalid');
    }
  }

  class WristMatchCompare extends HTMLElement {
    connectedCallback() {
      if (this.dataset.wristmatchCompareInitialized === 'true') return;
      this.dataset.wristmatchCompareInitialized = 'true';

      this.tray = this.querySelector('[data-wristmatch-compare-tray]');
      this.thumbnails = this.querySelector('[data-wristmatch-compare-thumbnails]');
      this.countOutput = this.querySelector('[data-wristmatch-compare-count]');
      this.statusOutput = this.querySelector('[data-wristmatch-compare-status]');
      this.openButton = this.querySelector('[data-wristmatch-compare-open]');
      this.clearButton = this.querySelector('[data-wristmatch-compare-clear]');
      this.dialog = this.querySelector('[data-wristmatch-compare-dialog]');
      this.closeButton = this.querySelector('[data-wristmatch-compare-close]');
      this.profileRequired = this.querySelector('[data-wristmatch-compare-profile-required]');
      this.content = this.querySelector('[data-wristmatch-compare-content]');
      this.conclusion = this.querySelector('[data-wristmatch-compare-conclusion]');
      this.grid = this.querySelector('[data-wristmatch-compare-grid]');
      if (!this.tray || !this.dialog || !this.grid) return;

      this.products = readCompareSelection();
      this.lastOpener = null;
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleComponentClick = this.handleComponentClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      document.addEventListener('click', this.handleDocumentClick);
      this.addEventListener('click', this.handleComponentClick);
      this.dialog.addEventListener('close', this.handleDialogClose);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      this.removeEventListener('click', this.handleComponentClick);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      delete this.dataset.wristmatchCompareInitialized;
    }

    refresh() {
      this.products = readCompareSelection();
      this.render();
    }

    render() {
      const profile = getSavedProfile();
      this.renderControls(profile);
      this.renderTray(profile);
      if (this.dialog.open) this.renderDialog(profile);
    }

    renderControls(profile) {
      document.querySelectorAll(compareToggleSelector).forEach((button) => {
        const productElement = button.closest(compareProductSelector);
        const product = getCompareProductFromElement(productElement);
        const isSelected = Boolean(product && this.products.some(({ id }) => id === product.id));
        const limitReached = this.products.length >= compareMaximum && !isSelected;

        button.hidden = !product;
        const label = button.querySelector('span:not(.mr-card-actions__tooltip)');
        if (label) label.textContent = isSelected ? 'Added to compare' : 'Compare with WristMatch';
        else button.textContent = isSelected ? '✓ Added to compare' : '+ Compare';
        button.setAttribute('aria-pressed', String(isSelected));
        button.setAttribute('aria-disabled', String(limitReached));
        if (product) {
          button.setAttribute(
            'aria-label',
            isSelected
              ? `Remove ${product.title} from WristMatch comparison`
              : `Add ${product.title} to WristMatch comparison`
          );
        }
      });
    }

    renderTray(profile) {
      const count = this.products.length;
      this.tray.hidden = count === 0 || !profile;
      if (!count || !profile) return;

      this.countOutput.textContent = count === 1 ? '1 watch selected' : `${count} watches selected`;
      this.openButton.textContent = count === 1 ? 'Select 1 more watch' : 'Compare WristMatch';
      this.openButton.disabled = count < 2;
      this.thumbnails.replaceChildren(...this.products.map((product) => this.createThumbnail(product)));
    }

    createThumbnail(product) {
      const thumbnail = document.createElement('span');
      thumbnail.className = 'mr-wristmatch-compare__thumbnail';

      if (product.imageUrl) {
        const image = document.createElement('img');
        image.src = product.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        thumbnail.appendChild(image);
      }

      const removeButton = document.createElement('button');
      removeButton.className = 'mr-wristmatch-compare__remove';
      removeButton.type = 'button';
      removeButton.dataset.wristmatchCompareRemove = product.id;
      removeButton.setAttribute('aria-label', `Remove ${product.title} from comparison`);
      removeButton.textContent = '×';
      thumbnail.appendChild(removeButton);
      return thumbnail;
    }

    handleDocumentClick(event) {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest(compareToggleSelector);
      if (!button || this.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();
      const profile = getSavedProfile();
      const productElement = button.closest(compareProductSelector);
      const product = getCompareProductFromElement(productElement);
      if (!product) {
        this.render();
        return;
      }
      if (!profile) {
        const mobileTriggerId = productElement.dataset.cardActionsTriggerId;
        const focusTarget = mobileTriggerId ? document.getElementById(mobileTriggerId) : button;
        this.openComparison(focusTarget || button, productElement);
        return;
      }

      const selectedIndex = this.products.findIndex(({ id }) => id === product.id);
      if (selectedIndex >= 0) {
        this.products.splice(selectedIndex, 1);
        this.saveAndRender();
        this.announce(`${product.title} removed from comparison.`);
        return;
      }

      if (this.products.length >= compareMaximum) {
        this.announce('You can compare up to 3 watches. Remove one before adding another.');
        return;
      }

      product.selectedAt = Date.now();
      this.products.push(product);
      this.saveAndRender();
      this.announce(`${product.title} added to comparison.`);

      const evaluation = evaluateProduct({
        length: product.length,
        shape: product.shape,
        thickness: product.thickness,
        width: product.width,
      }, profile.wristMm, profile.preference);
      publishWristMatchEvent(analyticsEvents.compareAdded, {
        source: getCompareSource(productElement),
        preference: preferences[profile.preference].label,
        match: evaluation?.styleMatch,
        fit_confidence: evaluation?.confidence,
        wristmatch_used: true,
        product_id: product.id,
        product_handle: product.handle,
        visible_position: this.products.length,
      });
    }

    handleComponentClick(event) {
      if (!(event.target instanceof Element)) return;

      const removeButton = event.target.closest('[data-wristmatch-compare-remove]');
      if (removeButton) {
        const product = this.products.find(({ id }) => id === removeButton.dataset.wristmatchCompareRemove);
        if (product) {
          this.products = this.products.filter(({ id }) => id !== product.id);
          this.saveAndRender();
          this.announce(`${product.title} removed from comparison.`);
        }
        return;
      }

      if (event.target.closest('[data-wristmatch-compare-clear]')) {
        this.products = [];
        writeCompareSelection(this.products);
        if (this.dialog.open) this.dialog.close();
        this.render();
        this.announce('WristMatch comparison cleared.');
        return;
      }

      const openButton = event.target.closest('[data-wristmatch-compare-open]');
      if (openButton) {
        if (this.products.length < 2) {
          this.announce('Select at least 2 watches to compare.');
          return;
        }
        this.openComparison(openButton);
        return;
      }

      if (event.target.closest('[data-wristmatch-compare-close]')) {
        this.dialog.close();
        return;
      }

      const productLink = event.target.closest('[data-wristmatch-compare-product-link]');
      if (productLink) this.trackComparedProductClick(productLink);
    }

    saveAndRender() {
      writeCompareSelection(this.products);
      this.render();
    }

    announce(message) {
      this.statusOutput.textContent = '';
      window.requestAnimationFrame(() => {
        this.statusOutput.textContent = message;
      });
    }

    openComparison(opener, sourceElement = opener) {
      const profile = getSavedProfile();
      this.lastOpener = opener;
      this.renderDialog(profile);
      if (!this.dialog.open) this.dialog.showModal();
      publishWristMatchEvent(analyticsEvents.compareOpened, {
        source: getCompareSource(sourceElement) || this.dataset.source,
        preference: profile ? preferences[profile.preference].label : undefined,
        wristmatch_used: Boolean(profile),
      });
    }

    handleDialogClose() {
      if (this.lastOpener?.isConnected) this.lastOpener.focus({ preventScroll: true });
      this.lastOpener = null;
    }

    renderDialog(profile) {
      this.profileRequired.hidden = Boolean(profile);
      this.content.hidden = !profile;
      this.grid.replaceChildren();
      if (!profile) return;

      const compared = this.products.map((product, index) => ({
        evaluation: evaluateProduct({
          length: product.length,
          shape: product.shape,
          thickness: product.thickness,
          width: product.width,
        }, profile.wristMm, profile.preference),
        index,
        product,
      }));
      const evaluable = compared.filter(({ evaluation }) => evaluation);
      const sorted = [...evaluable].sort((first, second) => this.compareEvaluations(first, second));
      let winnerId = '';

      if (evaluable.length < 2) {
        this.conclusion.textContent = 'WristMatch comparison requires published case width for at least two watches.';
      } else {
        const strongest = sorted[0];
        const reachesGood = ['Excellent match', 'Good match'].includes(strongest.evaluation.styleMatch);
        const tiedLeaders = sorted.filter((candidate) => this.areEffectivelyTied(strongest, candidate));

        if (tiedLeaders.length > 1 && reachesGood) {
          this.conclusion.textContent = tiedLeaders.length === 2
            ? 'Both are strong WristMatch options for your selected presence.'
            : 'These are similarly strong WristMatch options for your selected presence.';
        } else if (reachesGood) {
          winnerId = strongest.product.id;
          this.conclusion.textContent = `${strongest.product.title} is closest to your ${preferences[profile.preference].label} preference. Its proportions are the closest match to the wrist presence you selected.`;
        } else {
          this.conclusion.textContent = 'These watches are expected to wear differently from your selected presence. That can still be the right choice if you prefer their style.';
        }
      }

      this.grid.style.setProperty('--wristmatch-compare-count', String(Math.max(2, this.products.length)));
      this.grid.replaceChildren(...compared.map(({ evaluation, index, product }) => (
        this.createComparisonCard(product, evaluation, product.id === winnerId, index + 1, profile.preference)
      )));
    }

    compareEvaluations(first, second) {
      const confidenceRanks = { High: 0, Good: 1, Estimated: 2 };
      return recommendationRanks[first.evaluation.styleMatch] - recommendationRanks[second.evaluation.styleMatch]
        || first.evaluation.targetDistance - second.evaluation.targetDistance
        || confidenceRanks[first.evaluation.confidence] - confidenceRanks[second.evaluation.confidence]
        || first.index - second.index;
    }

    areEffectivelyTied(first, second) {
      return first.evaluation.styleMatch === second.evaluation.styleMatch
        && Math.abs(first.evaluation.targetDistance - second.evaluation.targetDistance) <= compareTieToleranceMm;
    }

    createComparisonCard(product, evaluation, isWinner, visiblePosition, preference) {
      const card = document.createElement('article');
      card.className = 'mr-wristmatch-compare__card';
      card.dataset.wristmatchBest = String(isWinner);

      if (isWinner) {
        const winner = document.createElement('span');
        winner.className = 'mr-wristmatch-compare__winner';
        winner.textContent = 'Closest to your fit preference';
        card.appendChild(winner);
      }

      if (product.imageUrl) {
        const imageLink = document.createElement('a');
        imageLink.className = 'mr-wristmatch-compare__image-link';
        imageLink.href = product.url;
        imageLink.setAttribute('aria-label', `View ${product.title}`);
        imageLink.dataset.wristmatchCompareProductLink = product.id;
        imageLink.dataset.visiblePosition = String(visiblePosition);
        const image = document.createElement('img');
        image.className = 'mr-wristmatch-compare__image';
        image.src = product.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        imageLink.appendChild(image);
        card.appendChild(imageLink);
      }

      const title = document.createElement('h3');
      title.className = 'mr-wristmatch-compare__product-title';
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      titleLink.dataset.wristmatchCompareProductLink = product.id;
      titleLink.dataset.visiblePosition = String(visiblePosition);
      title.appendChild(titleLink);
      card.appendChild(title);

      if (product.priceText) {
        const price = document.createElement('p');
        price.className = 'mr-wristmatch-compare__price';
        price.textContent = product.priceText;
        card.appendChild(price);
      }

      const details = document.createElement('dl');
      details.className = 'mr-wristmatch-compare__details';
      this.appendComparisonDetail(details, 'Expected wear', evaluation ? `${evaluation.expectedWear} on your wrist` : 'WristMatch unavailable');
      this.appendComparisonDetail(details, 'WristMatch', evaluation?.styleMatch || 'Unavailable');
      this.appendComparisonDetail(details, 'Fit confidence', evaluation?.confidence || 'Unavailable');
      this.appendComparisonDetail(details, 'Case width', `${formatPublishedDimension(product.width)} mm`);
      if (product.length) this.appendComparisonDetail(details, 'Case length', `${formatPublishedDimension(product.length)} mm`);
      if (product.thickness) this.appendComparisonDetail(details, 'Thickness', `${formatPublishedDimension(product.thickness)} mm`);
      if (product.shape) this.appendComparisonDetail(details, 'Case shape', product.shape);
      card.appendChild(details);

      if (evaluation) {
        const fitNote = document.createElement('p');
        fitNote.className = 'mr-wristmatch-compare__fit-note';
        fitNote.textContent = this.getComparisonFitNote(evaluation, preference);
        card.appendChild(fitNote);
      }

      const productLink = document.createElement('a');
      productLink.className = 'mr-wristmatch-compare__product-link';
      productLink.href = product.url;
      productLink.textContent = 'View watch';
      productLink.dataset.wristmatchCompareProductLink = product.id;
      productLink.dataset.visiblePosition = String(visiblePosition);
      card.appendChild(productLink);

      const removeButton = document.createElement('button');
      removeButton.className = 'mr-wristmatch-compare__card-remove';
      removeButton.type = 'button';
      removeButton.dataset.wristmatchCompareRemove = product.id;
      removeButton.textContent = 'Remove from comparison';
      card.appendChild(removeButton);
      return card;
    }

    getComparisonFitNote(evaluation, preference) {
      const selectedPreference = preferences[preference].label;
      if (evaluation.styleMatch === 'Excellent match') {
        return `Expected to wear close to your selected ${selectedPreference} presence.`;
      }

      const wearRanks = { 'Very compact': 0, Compact: 1, Balanced: 2, Bold: 3, 'Very bold': 4 };
      const preferenceRanks = { compact: 1, balanced: 2, bold: 3 };
      const direction = wearRanks[evaluation.expectedWear] < preferenceRanks[preference]
        ? 'more compactly'
        : 'more boldly';
      const degree = evaluation.styleMatch === 'Good match'
        ? 'a little'
        : evaluation.styleMatch === 'Style stretch' ? 'noticeably' : 'significantly';
      return `Expected to wear ${degree} ${direction} than your selected ${selectedPreference} presence.`;
    }

    appendComparisonDetail(list, label, value) {
      const row = document.createElement('div');
      row.className = 'mr-wristmatch-compare__detail';
      const term = document.createElement('dt');
      term.textContent = label;
      const description = document.createElement('dd');
      description.textContent = value;
      row.append(term, description);
      list.appendChild(row);
    }

    trackComparedProductClick(link) {
      const product = this.products.find(({ id }) => id === link.dataset.wristmatchCompareProductLink);
      const profile = getSavedProfile();
      if (!product || !profile) return;
      const evaluation = evaluateProduct({
        length: product.length,
        shape: product.shape,
        thickness: product.thickness,
        width: product.width,
      }, profile.wristMm, profile.preference);
      publishWristMatchEvent(analyticsEvents.compareProductClicked, {
        source: this.dataset.source,
        preference: preferences[profile.preference].label,
        match: evaluation?.styleMatch,
        fit_confidence: evaluation?.confidence,
        wristmatch_used: true,
        product_id: product.id,
        product_handle: product.handle,
        visible_position: Number.parseInt(link.dataset.visiblePosition, 10),
      });
    }
  }

  window.MundoRelojeroWristMatch = Object.assign(window.MundoRelojeroWristMatch || {}, {
    evaluateProductForSavedProfile(product) {
      const profile = getSavedProfile();
      if (!profile || !product) return null;
      return evaluateProduct({
        length: parseDimension(product.length),
        shape: String(product.shape || '').trim(),
        thickness: parseDimension(product.thickness),
        width: parseDimension(product.width),
      }, profile.wristMm, profile.preference);
    },
    hasSavedProfile() {
      return Boolean(getSavedProfile());
    },
    publishEvent(eventName, properties = {}) {
      publishWristMatchEvent(eventName, properties);
    },
  });

  customElements.define(elementName, WristMatch);
  if (!customElements.get(compareElementName)) customElements.define(compareElementName, WristMatchCompare);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    const item = event.target.closest(
      '[data-wristmatch-page-candidate], [data-wristmatch-recommendation], [data-wristmatch-collection-product]'
    );
    if (!link || !item || !item.contains(link)) return;

    const profile = getSavedProfile();
    if (!profile) return;

    const recommendationSection = item.closest(recommendationSelector);
    const isCollectionProduct = item.matches(collectionItemSelector);
    const source = recommendationSection
      ? 'product_recommendations'
      : isCollectionProduct
        ? 'collection'
        : 'calculator';
    if (recommendationSection && recommendationSection.dataset.wristmatchPersonalized !== 'true') {
      evaluateRecommendationSection(recommendationSection);
    }
    if (recommendationSection && recommendationSection.dataset.wristmatchPersonalized !== 'true') return;
    if (isCollectionProduct) evaluateCollectionProducts();
    if (!item.dataset.wristmatchMatch) return;

    const siblings = [...(item.parentElement?.children || [])];
    const visiblePosition = Number.parseInt(item.dataset.wristmatchVisiblePosition, 10)
      || siblings.indexOf(item) + 1;
    publishWristMatchEvent(
      analyticsEvents.matchedProductClicked,
      {
        source,
        preference: preferences[profile.preference].label,
        match: item.dataset.wristmatchMatch,
        expected_wear: item.dataset.wristmatchExpectedWear,
        fit_confidence: item.dataset.wristmatchFitConfidence,
        display_unit: profile.unit,
        wristmatch_used: true,
        wristmatch_pick: item.dataset.wristmatchPick === 'true',
        product_id: item.dataset.productId,
        product_handle: item.dataset.productHandle,
        variant_id: item.dataset.variantId,
        visible_position: visiblePosition,
      }
    );
  });

  const wristMatchObserver = new MutationObserver((mutations) => {
    const containsWristMatchContent = mutations.some(({ addedNodes }) => [...addedNodes].some((node) => (
      node.nodeType === Node.ELEMENT_NODE
      && (
        node.matches(recommendationSelector)
        || node.matches(recommendationItemSelector)
        || node.matches(collectionItemSelector)
        || node.matches(compareProductSelector)
        || node.querySelector(recommendationSelector)
        || node.querySelector(recommendationItemSelector)
        || node.querySelector(collectionItemSelector)
        || node.querySelector(compareProductSelector)
      )
    )));

    if (containsWristMatchContent) scheduleRecommendationEvaluation();
  });

  wristMatchObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('storage', (event) => {
    if (event.key === null || Object.values(storageKeys).includes(event.key)) {
      scheduleRecommendationEvaluation();
    }
  });
  scheduleRecommendationEvaluation();
})();
